import React, { useContext, useEffect, useState } from 'react';
import { Container } from 'typescript-ioc';
import classnames from 'classnames';
import { FormattedMessage, injectIntl, IntlShape } from 'react-intl';

import { IRouter } from '../../../infrastructure/router';
import { IAccountsDAO } from '../../../api/accounts.dao';
import { SearchInput } from '../search-input/search-input';

import { Routes } from '../../../routes-table';

import { ChevronDownIcon, ChevronUpIcon } from '../icons/icons';
import { COMMON_ITEMS_PER_PAGE } from '../../../constants/pagination.contants';

import CrownIconSvg from '../../../images/svg/icons8-crown';
import './account-selector.scss';
import { MINIMAL_SEARCH_LENGTH } from '../../../constants/common.constants';
import { getMainNavigationLinks } from '../../../constants/navigation.constants';
import { IEventDetailsDTO } from '../../../api/events.dao';
import { EventDetailsContext } from '../../global.context';
import { IGlobalStorage } from '../../../infrastructure/global-storage';
import { LOADING } from '../../../constants/common-labels.constants';


export interface ButtonProp {
    className?: string | null;
    disabled?: boolean;
    intl: IntlShape;
}

const AccountSelector = React.forwardRef((props: ButtonProp, ref: React.Ref<HTMLButtonElement>) => {
    const { className, disabled, intl } = props;
    const accountsDAO = Container.get(IAccountsDAO);

    const context = useContext(EventDetailsContext);
    const eventDetails: IEventDetailsDTO = context?.eventDetails;

    const [isOpen, toggleOptions] = useState(false);
    const [isFetching, setFetching] = useState(false);
    const [accounts, setAccounts] = useState([]);
    const [selectedAccount, setAccount] = useState(null);
    const [filteredAccounts, setFilteredAccounts] = useState([]);

    const routingService = Container.get(IRouter);
    const params = routingService.router.getState().params;
    const routeAccountId = params.accountId;
    const routeEventId = params.eventId;

    const hasAdminPermission = !!getMainNavigationLinks('', intl).filter(el => !el.isHidden).length;

    useEffect(() => {
        let isSubscribed = true;
        /* [OEC-8670] Due to the permission restrictions fetching account available for all users from the "account level" */
        if (routeAccountId && !routeEventId) {
            accountsDAO.readAccount(routeAccountId).then(account => {
                if (isSubscribed) {
                    setAccount(account);
                    Container.get(IGlobalStorage).setAccount(account);
                }
            }).catch(error => {
                if (error.response.status === 403) {
                    routingService.navigate(Routes.AccountsList, {});
                }
            });
        /* [OEC-8670] Getting the current account info from the event details set to the context */
        } else if (routeAccountId && routeEventId && !selectedAccount) {
            const accountDetails = eventDetails ? { accountId: eventDetails?.accountId, name: eventDetails?.accountName } : null;
            setAccount(accountDetails);
            Container.get(IGlobalStorage).setAccount(accountDetails);
        } else {
            setAccount(null);
        }

        return () => isSubscribed = false;
    }, [routeAccountId, eventDetails]);

    const fetchAccounts = async (query?: string) => {
        if (!query) setFetching(true);

        const searchParams = {
            page: 0,
            size: COMMON_ITEMS_PER_PAGE,
            query: query || ''
        };

        const data = await accountsDAO.getUserAccounts(searchParams);
        if (!query) setAccounts(data.content);
        setFilteredAccounts(data.content);
        setFetching(false);
    };

    const handleSearchChange = (e, value = '') => {
        const trimmedQuery = value.trim();
        if (trimmedQuery.length >= MINIMAL_SEARCH_LENGTH) {
            fetchAccounts(trimmedQuery);
        }
    };

    const onAccSelectorClick = () => {
        toggleOptions(!isOpen);
        fetchAccounts();
    };

    const onOptionClick = accountId => {
        toggleOptions(false);
        routingService.navigate(Routes.EventsList, {
            accountId: String(accountId)
        });
    };

    const onAdminClick = () => {
        toggleOptions(false);
        const routesList = getMainNavigationLinks('', intl);
        const routeEnum = routesList.find(route => !route.isHidden)?.routeEnum || Routes.AccountsList;
        routingService.navigate(routeEnum, {});
    };

    const accountName = selectedAccount && selectedAccount.name;

    const btnClasses = classnames(
        'default-button options-button default-button--account-selector default-button--fat default-button--options',
        { 'options-button--active': isOpen },
        { 'options-button--without-selected-account': !routeAccountId && hasAdminPermission },
        className
    );

    const noAccountsPlaceholder = (
        <div className='options-button__placeholder'>
            <div>
                <FormattedMessage
                    id='oe.accounts.dontHaveAnyAccounts'
                    description='No Account placeholder'
                    defaultMessage="You don't have any accounts yet."
                />
            </div>
            <div>
                <FormattedMessage
                    id='oe.common.createOneAndProceed'
                    description='Create some account placeholder'
                    defaultMessage='Please create one and proceed.'
                />
            </div>
        </div>
    );

    const loadingPlaceholder = (
        <div className='options-button__placeholder'>
            <FormattedMessage
                {...LOADING}
                values={{
                    suffix: '...'
                }}
            />
        </div>
    );

    return (
        <div className='account-selector'>
            <button
                ref={ref}
                className={btnClasses}
                disabled={disabled}
                onClick={() => toggleOptions(!isOpen)}
            >
                <span
                    className='options-button__label'
                    onClick={onAccSelectorClick}
                >
                    <div className='options-button__text'>
                        {accountName || hasAdminPermission && (
                            <div className='options-button__admin'>
                                <CrownIconSvg />
                                <div className='options-button__name'>
                                    <FormattedMessage
                                        id='oe.common.admin'
                                        description='Selector Admin label'
                                        defaultMessage='Admin'
                                    />
                                </div>
                            </div>
                        )}
                    </div>

                    {isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
                </span>
            </button>
            {isOpen &&
                <>
                    <div className='options-button__overlay' onClick={onAccSelectorClick} />
                    <div className='options-button__options'>
                        <>
                            {routeAccountId && hasAdminPermission && (
                                <div
                                    className='options-button__admin-list'
                                    key='admin'
                                    onClick={onAdminClick}
                                >
                                    <CrownIconSvg />
                                    <div className='options-button__name'>
                                        <FormattedMessage
                                            id='oe.common.admin'
                                            description='Selector Admin label'
                                            defaultMessage='Admin'
                                        />
                                    </div>
                                </div>
                            )}
                            {accounts.length ?
                                <>
                                    <div className='options-button__search'>
                                        <SearchInput
                                            placeholder={intl.formatMessage(
                                                {
                                                    id: 'oe.accounts.searchAccounts',
                                                    description: 'Search accounts input placeholder',
                                                    defaultMessage: 'Search Accounts',
                                                }
                                            )}
                                            onChange={handleSearchChange}
                                            resetSearch={fetchAccounts}
                                            withDebounce
                                        />
                                    </div>

                                    <div className='options-button__accounts'>
                                        {filteredAccounts.length ?
                                            filteredAccounts.map(account => {
                                                const optionsClasses = classnames('options-button__option', {
                                                    'options-button__option--selected': String(routeAccountId) === String(account.accountId)
                                                });

                                                return (
                                                    <div
                                                        className={optionsClasses}
                                                        key={account.accountId}
                                                        onClick={() => onOptionClick(account.accountId)}
                                                    >
                                                        {account.name}
                                                    </div>
                                                );
                                            }) : <div className='options-button__placeholder'>{intl.formatMessage({
                                                id: 'oe.accounts.noAccountsFound',
                                                defaultMessage: 'No accounts found.',
                                                description: 'Empty search placeholder text',
                                            })}</div>
                                        }
                                    </div>
                                </> : isFetching ? loadingPlaceholder : noAccountsPlaceholder
                            }
                        </>
                    </div>
                </>
            }
        </div>
    );
});

export default injectIntl(AccountSelector);
